import { useId } from 'react';
import { Input } from '@dataroom/shared';
import { parseOwnershipPct } from '@/lib/ownership';

/**
 * A seller's "Ownership %" input with its plain-language check: required, above 0,
 * at most 100, at most three decimals. Controlled; the caller reads the same
 * `parseOwnershipPct` to decide whether it may submit.
 */
export function OwnershipPctField({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = `${inputId}-hint`;
  const { problem } = parseOwnershipPct(value);
  const showProblem = value.trim() !== '' && problem !== null;
  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="text-sm font-medium">
        Ownership %
      </label>
      <div className="flex items-center gap-2">
        <Input
          id={inputId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder="e.g. 60"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-describedby={hintId}
          aria-invalid={showProblem ? true : undefined}
          className="w-28"
        />
        <span className="text-sm text-muted-foreground" aria-hidden="true">
          %
        </span>
      </div>
      <p
        id={hintId}
        role={showProblem ? 'alert' : undefined}
        className={showProblem ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}
      >
        {showProblem
          ? problem
          : 'Their share of the company: above 0, up to 100, at most three decimals.'}
      </p>
    </div>
  );
}
